/**
 * Shared pair → assets roster for Resupply, written by the public-data fetcher
 * and read by the oracle-price fetcher.
 *
 * Same reason Curvance needs one (see `curvanceRoster.ts`): most oracle
 * fetchers resolve their market list from `data-sdk` config, but Resupply
 * publishes no roster — its pairs are DISCOVERED on-chain from
 * `ResupplyRegistry.getAllPairAddresses()`, and `getCalls` is synchronous, so
 * the price fetcher cannot discover them itself.
 *
 * Re-discovering here would also be wrong rather than merely wasteful: the
 * price pass would key entries to a pair list that could differ from the one
 * the data pass just published, so a newly added pair would get rows with no
 * prices or prices with no rows.
 *
 * Cold start: on a process's first refresh the roster is empty and the fetcher
 * contributes nothing. That is safe — reUSD simply has no price for one cycle,
 * exactly as it did before this existed, and the public fetcher fills the
 * roster on the same pass.
 */

export interface ResupplyPairAssets {
  /** The `ResupplyPair` contract — the market. */
  pair: string
  /** Lowercase crvUSD / frxUSD — the collateral leg our rows publish. */
  underlying: string
}

const rosterCache = new Map<string, ResupplyPairAssets[]>()

const key = (chainId: string, lender: string) => `${chainId}:${lender}`

/** Publish the discovered roster. Called by the public-data fetcher. */
export function setResupplyPairRoster(
  chainId: string,
  lender: string,
  pairs: ResupplyPairAssets[],
): void {
  // An empty result almost always means the registry read failed rather than
  // that governance removed every pair (it never removes any — retiring one
  // zeroes `borrowLimit`), so keep the previous roster instead of blanking the
  // price fetcher for a cycle.
  if (pairs.length === 0) return
  rosterCache.set(key(chainId, lender), pairs)
}

/** Read the roster. Empty until the public-data fetcher has run once. */
export function getResupplyPairRoster(
  chainId: string,
  lender: string,
): ResupplyPairAssets[] {
  return rosterCache.get(key(chainId, lender)) ?? []
}

/** Test seam — drop the cache so a suite starts cold. */
export function __resetResupplyPairRoster(): void {
  rosterCache.clear()
}
